import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Alert,
  FormControl, InputLabel, Select, MenuItem, Stack,
  CircularProgress, Divider, TextField, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import EditIcon   from '@mui/icons-material/Edit';
import SaveIcon   from '@mui/icons-material/Save';
import CloseIcon  from '@mui/icons-material/Close';
import PasswordDialog from './PasswordDialog';
import ConfirmDialog  from './ConfirmDialog';
import { getReceipts, deleteReceipt, getReceiptImageUrl, getBankStatementImageUrl, revertExport, updateReceipt, getEvents, getStores } from '../api';

const CATEGORIES = [
  { value: 'spelmateriaal',     label: 'Spelmateriaal' },
  { value: 'Fourage',           label: 'Fourage' },
  { value: 'Onderhoud vletten', label: 'Onderhoud vletten' },
  { value: 'Onderhoud anders',  label: 'Onderhoud anders' },
  { value: 'Diversen',          label: 'Diversen' },
];

// Event (Hike/Kamp) receipts use the camp categories that match the event template.
const EVENT_CATEGORIES = [
  { value: 'Eten & drinken',      label: 'Eten & drinken' },
  { value: 'Programma / Spel',    label: 'Programma / Spel' },
  { value: 'Lokatie',             label: 'Lokatie' },
  { value: 'Brandstof / Vervoer', label: 'Brandstof / Vervoer' },
  { value: 'Algemeen',            label: 'Algemeen' },
];

function ReceiptList({ members, activeGroup, groupPassword, refreshTrigger }) {
  const [receipts, setReceipts]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filterMember, setFilterMember] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterExported, setFilterExported] = useState('pending');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [events, setEvents]             = useState([]);
  const [stores, setStores]             = useState([]);

  // Edit-mode state for detail dialog
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm]   = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError]   = useState('');

  // Dialog state
  const [revertPwdDialog, setRevertPwdDialog]         = useState({ open: false, receiptId: null });
  const [revertConfirmDialog, setRevertConfirmDialog] = useState({ open: false, receiptId: null });
  const [deleteDialog, setDeleteDialog]               = useState({ open: false, receiptId: null, name: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadReceipts(); }, [refreshTrigger, filterMember, activeGroup]);

  // Load events + stores once per group (used by the edit form)
  useEffect(() => {
    if (!activeGroup) return;
    getEvents(activeGroup).then(setEvents).catch(() => {});
    getStores(activeGroup).then(r => setStores(r.stores || [])).catch(() => {});
  }, [activeGroup]);

  const loadReceipts = async () => {
    setLoading(true);
    try {
      const filters = { group_name: activeGroup };
      if (filterMember) filters.member_id = filterMember;
      setReceipts(await getReceipts(filters));
    } catch (err) { console.error('Fout bij laden van bonnetjes:', err); }
    finally { setLoading(false); }
  };

  // ── Client-side filtering ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = receipts;
    if (filterCategory)          result = result.filter(r => r.category === filterCategory);
    if (filterExported === 'pending') result = result.filter(r => r.status !== 'exported');
    return result;
  }, [receipts, filterCategory, filterExported]);

  // ── Group by month (newest first) ────────────────────────────────────────
  const monthGroups = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const d = new Date(r.date);
      const key   = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('nl-NL', { year: 'numeric', month: 'long' });
      if (!map[key]) map[key] = { label: label.charAt(0).toUpperCase() + label.slice(1), items: [] };
      map[key].items.push(r);
    });
    return Object.entries(map).sort(([a], [b]) => b.localeCompare(a)).map(([, v]) => v);
  }, [filtered]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    try { await deleteReceipt(deleteDialog.receiptId); loadReceipts(); }
    catch (err) { alert('Fout bij verwijderen: ' + err.message); }
    setDeleteDialog({ open: false, receiptId: null, name: '' });
  };

  const handleRevertPasswordConfirm = (password) => {
    if (password !== groupPassword) {
      setRevertPwdDialog({ open: false, receiptId: null });
      alert('Onjuist wachtwoord!');
      return;
    }
    const receiptId = revertPwdDialog.receiptId;
    setRevertPwdDialog({ open: false, receiptId: null });
    setRevertConfirmDialog({ open: true, receiptId });
  };

  const handleRevertConfirm = async () => {
    try { await revertExport(revertConfirmDialog.receiptId); loadReceipts(); }
    catch (err) { alert('Fout bij terugzetten: ' + err.message); }
    setRevertConfirmDialog({ open: false, receiptId: null });
  };

  const closeDetail = () => {
    setSelectedReceipt(null);
    setIsEditing(false);
    setEditForm(null);
    setEditError('');
  };

  const startEdit = () => {
    if (!selectedReceipt) return;
    setEditError('');
    setEditForm({
      member_id: selectedReceipt.member_id ?? '',
      amount:    selectedReceipt.amount ?? '',
      category:  selectedReceipt.category ?? 'Fourage',
      store:     selectedReceipt.store ?? '',
      remarks:   selectedReceipt.remarks ?? '',
      event_id:  selectedReceipt.event_id ?? '',
      date:      selectedReceipt.date ? selectedReceipt.date.split('T')[0] : '',
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditForm(null);
    setEditError('');
  };

  const saveEdit = async () => {
    if (!selectedReceipt || !editForm) return;
    if (!editForm.member_id || !editForm.amount || !editForm.category || !editForm.date) {
      setEditError('Staflid, bedrag, categorie en datum zijn verplicht.');
      return;
    }
    setSavingEdit(true);
    setEditError('');
    try {
      const updated = await updateReceipt(selectedReceipt.id, {
        member_id: editForm.member_id,
        amount:    parseFloat(editForm.amount),
        category:  editForm.category,
        store:     editForm.store || '',
        remarks:   editForm.remarks || '',
        // Send an explicit sentinel to clear the event — an empty multipart
        // field reaches the backend as None (indistinguishable from "unchanged").
        event_id:  editForm.event_id === '' ? 'null' : editForm.event_id,
        date:      editForm.date,
      });
      // Merge update back into selectedReceipt for immediate display
      setSelectedReceipt(prev => ({ ...prev, ...updated }));
      setIsEditing(false);
      setEditForm(null);
      loadReceipts();
    } catch (err) {
      setEditError(err?.response?.data?.detail || err.message || 'Fout bij opslaan');
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const formatDate = (s) => new Date(s).toLocaleDateString('nl-NL');
  const formatCurrency = (n) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n);
  const monthTotal = (items) => items.reduce((s, r) => s + (r.amount || 0), 0);

  const statusChip = (status) => {
    const map = {
      exported:  { label: 'Geëxporteerd', color: 'success' },
      confirmed: { label: 'Bevestigd',    color: 'primary' },
    };
    const c = map[status] || { label: status, color: 'default' };
    return <Chip label={c.label} color={c.color} size="small" />;
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Paper elevation={1} sx={{ borderRadius: 2, p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Bonnetjes Overzicht</Typography>

      {/* Filters */}
      <Stack direction="row" spacing={1.5} mb={2.5} flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 130 }}>
          <InputLabel>Staflid</InputLabel>
          <Select value={filterMember} label="Staflid" onChange={e => setFilterMember(e.target.value)}>
            <MenuItem value="">Alle stafleden</MenuItem>
            {members.filter(m => m.name !== 'Voorschot').map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 130 }}>
          <InputLabel>Categorie</InputLabel>
          <Select value={filterCategory} label="Categorie" onChange={e => setFilterCategory(e.target.value)}>
            <MenuItem value="">Alle categorieën</MenuItem>
            <MenuItem value="spelmateriaal">Spelmateriaal</MenuItem>
            <MenuItem value="Fourage">Fourage</MenuItem>
            <MenuItem value="Onderhoud vletten">Onderhoud vletten</MenuItem>
            <MenuItem value="Onderhoud anders">Onderhoud anders</MenuItem>
            <MenuItem value="Diversen">Diversen</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterExported} label="Status" onChange={e => setFilterExported(e.target.value)}>
            <MenuItem value="pending">Niet geëxporteerd</MenuItem>
            <MenuItem value="all">Alle bonnetjes</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* List */}
      {loading ? (
        <Box textAlign="center" py={4}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Box textAlign="center" py={5} color="text.disabled">
          <Typography fontSize="4rem">📭</Typography>
          <Typography variant="body1" mt={1}>
            {filterExported === 'pending' ? 'Geen ongeëxporteerde bonnetjes' : 'Geen bonnetjes gevonden'}
          </Typography>
          {filterExported === 'pending' && receipts.length > 0 && (
            <Typography variant="body2" mt={1} color="text.disabled">
              Er zijn wel {receipts.length} geëxporteerde bonnetjes —{' '}
              <Button variant="text" size="small" sx={{ p: 0, minHeight: 'auto', verticalAlign: 'baseline' }}
                onClick={() => setFilterExported('all')}>
                toon alles
              </Button>
            </Typography>
          )}
        </Box>
      ) : (
        <Box>
          {monthGroups.map(group => (
            <Box key={group.label} mb={1}>
              {/* Month header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.75, borderBottom: 2, borderColor: 'primary.main', mb: 1, mt: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="primary">{group.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.items.length} {group.items.length === 1 ? 'bonnetje' : 'bonnetjes'} · {formatCurrency(monthTotal(group.items))}
                </Typography>
              </Box>

              {group.items.map(receipt => (
                <Paper key={receipt.id} variant="outlined" sx={{ mb: 1, borderRadius: 1.5, p: { xs: 1.5, sm: 2 }, '&:hover': { boxShadow: 2 }, transition: 'box-shadow 0.2s' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                    <Box flex={1}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {formatDate(receipt.date)} — {receipt.member_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{formatCurrency(receipt.amount)}</strong> · {receipt.category}
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" mt={0.5} useFlexGap>
                        {statusChip(receipt.status)}
                        <Chip
                          label={receipt.event_id ? `📅 ${receipt.event_title}` : '📋 Reguliere opkomst'}
                          size="small"
                          variant="outlined"
                          color={receipt.event_id ? 'primary' : 'success'}
                        />
                        {receipt.receipt_type === 'missing_receipt' && (
                          <Chip label="⚠️ Alternatief bewijs" size="small" color="warning" variant="outlined" />
                        )}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="flex-start" flexShrink={0} flexWrap="wrap" useFlexGap>
                      <Button size="small" variant="outlined" color="inherit" onClick={() => setSelectedReceipt(receipt)}>Bekijken</Button>
                      {receipt.status === 'exported' && (
                        <Button size="small" variant="contained" color="warning"
                          onClick={() => setRevertPwdDialog({ open: true, receiptId: receipt.id })}>
                          Terugzetten
                        </Button>
                      )}
                      <Button size="small" variant="contained" color="error"
                        onClick={() => setDeleteDialog({ open: true, receiptId: receipt.id, name: `${receipt.member_name} - ${formatDate(receipt.date)}` })}>
                        Verwijderen
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Box>
          ))}
        </Box>
      )}

      {/* Detail modal */}
      <Dialog open={!!selectedReceipt} onClose={closeDetail} maxWidth="sm" fullWidth>
        {selectedReceipt && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
              <span>Bonnetje #{selectedReceipt.receipt_number}</span>
              {!isEditing && selectedReceipt.status !== 'exported' && (
                <Tooltip title="Bewerken">
                  <IconButton size="small" onClick={startEdit} aria-label="Bewerken">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </DialogTitle>
            <DialogContent>
              {selectedReceipt.receipt_type === 'missing_receipt' ? (
                <>
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    <strong>⚠️ Alternatief bewijs</strong> — origineel bonnetje niet beschikbaar
                  </Alert>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap">
                    <Box flex={1} minWidth={140}>
                      <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>Lijst gekochte items</Typography>
                      <Box component="img" src={getReceiptImageUrl(selectedReceipt.id)} alt="Items" sx={{ maxWidth: '100%', borderRadius: 1 }} />
                    </Box>
                    {selectedReceipt.has_bank_statement && (
                      <Box flex={1} minWidth={140}>
                        <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>Bankafschrift</Typography>
                        <Box component="img" src={getBankStatementImageUrl(selectedReceipt.id)} alt="Bankafschrift" sx={{ maxWidth: '100%', borderRadius: 1 }} />
                      </Box>
                    )}
                  </Stack>
                </>
              ) : (
                <Box component="img" src={getReceiptImageUrl(selectedReceipt.id)} alt="Bonnetje"
                  sx={{ maxWidth: '100%', borderRadius: 1, mb: 1.5, display: 'block' }} />
              )}
              <Divider sx={{ my: 1.5 }} />

              {isEditing && editForm ? (
                <Stack spacing={1.5}>
                  {editError && <Alert severity="error">{editError}</Alert>}

                  <FormControl fullWidth size="small">
                    <InputLabel>Staflid *</InputLabel>
                    <Select value={editForm.member_id} label="Staflid *"
                      onChange={e => setEditForm(f => ({ ...f, member_id: e.target.value }))}>
                      {members.filter(m => m.name !== 'Voorschot').map(m => (
                        <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField fullWidth size="small" label="Bedrag (€) *" type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    value={editForm.amount}
                    onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))} />

                  <FormControl fullWidth size="small">
                    <InputLabel>Categorie *</InputLabel>
                    <Select value={editForm.category} label="Categorie *"
                      onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}>
                      {(editForm.event_id !== '' && editForm.event_id != null ? EVENT_CATEGORIES : CATEGORIES)
                        .map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                    </Select>
                  </FormControl>

                  <TextField fullWidth size="small" label="Datum *" type="date"
                    InputLabelProps={{ shrink: true }}
                    value={editForm.date}
                    onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />

                  <TextField fullWidth size="small" label="Winkel" placeholder="bv. Jumbo, Action"
                    value={editForm.store}
                    onChange={e => setEditForm(f => ({ ...f, store: e.target.value }))}
                    inputProps={{ list: 'edit-store-options' }} />
                  <datalist id="edit-store-options">
                    {stores.map((s, i) => <option key={i} value={s} />)}
                  </datalist>

                  <TextField fullWidth size="small" label="Opmerkingen" multiline minRows={2}
                    value={editForm.remarks}
                    onChange={e => setEditForm(f => ({ ...f, remarks: e.target.value }))} />

                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select value={editForm.event_id === null ? '' : editForm.event_id} label="Type"
                      onChange={e => setEditForm(f => {
                        const nextEvent = e.target.value;
                        const validCats = (nextEvent !== '' && nextEvent != null) ? EVENT_CATEGORIES : CATEGORIES;
                        // Keep the category if it still fits the new type, else reset to a valid default.
                        const category = validCats.some(c => c.value === f.category) ? f.category : validCats[0].value;
                        return { ...f, event_id: nextEvent, category };
                      })}>
                      <MenuItem value="">📋 Reguliere opkomst</MenuItem>
                      {events.map(ev => (
                        <MenuItem key={ev.id} value={ev.id}>📅 {ev.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              ) : (
                <Stack spacing={0.5}>
                  {[
                    ['Staflid',     selectedReceipt.member_name],
                    ['Bedrag',      formatCurrency(selectedReceipt.amount)],
                    ['Categorie',   selectedReceipt.category],
                    ['Datum',       formatDate(selectedReceipt.date)],
                    ['Winkel',      selectedReceipt.store || '—'],
                    ['Opmerkingen', selectedReceipt.remarks || '—'],
                    ['Status',      selectedReceipt.status],
                    ['Type',        selectedReceipt.event_id ? `Evenement: ${selectedReceipt.event_title}` : 'Reguliere opkomst'],
                  ].map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600} minWidth={100}>{k}:</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              {isEditing ? (
                <>
                  <Button onClick={cancelEdit} color="inherit" startIcon={<CloseIcon />} disabled={savingEdit}>
                    Annuleren
                  </Button>
                  <Button onClick={saveEdit} variant="contained" color="success"
                    startIcon={<SaveIcon />} disabled={savingEdit}>
                    {savingEdit ? 'Opslaan...' : 'Opslaan'}
                  </Button>
                </>
              ) : (
                <Button onClick={closeDetail} color="inherit">Sluiten</Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Password dialog for revert */}
      <PasswordDialog
        open={revertPwdDialog.open}
        onClose={() => setRevertPwdDialog({ open: false, receiptId: null })}
        onConfirm={handleRevertPasswordConfirm}
        title="Bonnetje terugzetten"
        description="Voer het wachtwoord in om dit bonnetje terug te zetten naar 'bevestigd'."
      />

      {/* Confirm revert */}
      <ConfirmDialog
        open={revertConfirmDialog.open}
        onClose={() => setRevertConfirmDialog({ open: false, receiptId: null })}
        onConfirm={handleRevertConfirm}
        title="Bonnetje terugzetten?"
        message="Weet je zeker dat je dit bonnetje terug wilt zetten naar 'bevestigd'?"
        confirmText="Terugzetten"
        confirmColor="warning"
      />

      {/* Confirm delete */}
      <ConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, receiptId: null, name: '' })}
        onConfirm={handleDeleteConfirm}
        title="Bonnetje verwijderen?"
        message={`Weet je zeker dat je "${deleteDialog.name}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`}
        confirmText="Verwijderen"
        confirmColor="error"
      />
    </Paper>
  );
}

export default ReceiptList;
